import { redirectToLogin } from '@/utils/redirect';
import { ApolloClient, InMemoryCache, createHttpLink, ApolloLink, from, fromPromise } from '@apollo/client';

import { onError } from '@apollo/client/link/error';

import { getAccessToken } from '@/services/auth/token-store';
import { refreshAccessToken } from '@/services/auth/refresh';

const possibleTypes = {
  PodcastDefaultContentTemplateDynamicZone: [
    'ComponentCommonAudio',
    'ComponentCommonAuthor',
    'ComponentCommonAuthorDetailed',
    'ComponentCommonBullet',
    'ComponentCommonDivider',
    'ComponentCommonDownloadableContentList',
    'ComponentCommonEmbedded',
    'ComponentCommonFaqComponent',
    'ComponentCommonH3',
    'ComponentCommonMedia',
    'ComponentCommonQuote',
    'ComponentCommonRelatedContent',
    'ComponentCommonTags',
    'ComponentCommonTextEditor',
    'ComponentCommonTitle',
    'ComponentPodcastPodcastRelated',
    'Error',
  ],
  ResourceDefaultContentTemplateDynamicZone: [
    'ComponentCommonAudio',
    'ComponentCommonAuthor',
    'ComponentCommonAuthorDetailed',
    'ComponentCommonDivider',
    'ComponentCommonDownloadableContentList',
    'ComponentCommonEmbedded',
    'ComponentCommonFaqComponent',
    'ComponentCommonH3',
    'ComponentCommonQuote',
    'ComponentCommonRelatedContent',
    'ComponentCommonTags',
    'ComponentCommonTextEditor',
    'ComponentCommonTitle',
    'ComponentResourceAssets',
    'ComponentResourceResourcesRelated',
    'Error',
  ],
};

const errorLink = onError((error) => {
  const { graphQLErrors, networkError, operation, forward } = error;
  console.error({ error });

  const skipAuthRedirect = Boolean(operation.getContext().skipAuthRedirect);

  const hasAuthGraphQLError = Boolean(
    graphQLErrors?.some(({ message }) => message === 'UNAUTHENTICATED' || message === 'Forbidden access'),
  );

  const hasAuthNetworkError = Boolean(
    (networkError && 'response' in networkError && networkError.response.statusText.includes('Unauthorized')) ||
    (networkError && 'statusCode' in networkError && networkError.statusCode === 401),
  );

  if (!hasAuthGraphQLError && !hasAuthNetworkError) {
    return;
  }

  // Operations can opt out of auth handling (e.g. the public latest-updates
  // panel): never redirect to login or refresh the token for them — let the
  // error pass through untouched.
  if (skipAuthRedirect) {
    return;
  }

  // Access tokens now last 15 minutes, so a 401 is usually just expiry rather
  // than a dead session. Renew once and replay before giving up on the user.
  const ctx = operation.getContext() as { authRetried?: boolean };

  if (ctx.authRetried) {
    // Already renewed once and still refused: the session really is gone.
    // Retrying again would loop indefinitely against a permanent 401.
    redirectToLogin();
    return;
  }

  return fromPromise(
    refreshAccessToken().catch(() => {
      redirectToLogin();
      return null;
    }),
  )
    .filter((token): token is string => Boolean(token))
    .flatMap((token) => {
      operation.setContext({
        authRetried: true,
        headers: { authorization: `Bearer ${token}` },
      });

      return forward(operation);
    });
});

const httpLink = createHttpLink({
  uri: `${import.meta.env.VITE_API_URL}/graphql`,
});

const authLink = new ApolloLink((operation, forward) => {
  const ctx = operation.getContext() as { headers?: Record<string, string>; skipAuth?: boolean };
  // In-memory, not localStorage: an httpOnly refresh cookie is pointless if a
  // working bearer token sits in storage beside it.
  const token = getAccessToken();
  if (!ctx.skipAuth) {
    operation.setContext({
      headers: {
        authorization: token ? `Bearer ${token}` : '',
      },
    });
  }
  return forward(operation);
});

export const client = new ApolloClient({
  // errorLink must sit before the terminating httpLink: httpLink never calls
  // forward, so any link placed after it never runs. With the wrong order the
  // 401 refresh-and-replay handling below was silently dead and expired tokens
  // bounced straight to /login instead of being renewed.
  link: from([authLink, errorLink, httpLink]),
  cache: new InMemoryCache({
    possibleTypes,
  }),
  defaultOptions: {
    query: {
      fetchPolicy: 'network-only',
    },
  },
});

export const initializeApollo = () => {
  return client;
};

export const initializeApolloWithoutToken = () => {
  return new ApolloClient({
    link: from([errorLink, httpLink]),
    cache: new InMemoryCache({
      possibleTypes,
    }),
  });
};
